import classNames from 'classnames';
import { type ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import styles from './plot-tooltip.module.css';
import { usePlotTooltipPortalEnabled } from './plot-tooltip-portal-context';

export type TooltipPosition = {
  x: number;
  y: number;
};

type PlotTooltipProps = {
  isVisible: boolean;
  children: ReactNode;
} & TooltipPosition;

/**
 * Two render modes:
 * - **Inline** (default): `position: absolute` inside the chart's DOM.
 * - **Portalled** (opt-in via `PlotTooltipPortalProvider`): `position: fixed`
 *   in `document.body` so the tooltip can escape ancestors that clip
 *   overflow — see the provider for the rationale.
 *
 * Both modes flip the tooltip to the cursor's left half when the cursor
 * sits in the right half of the viewport, so it doesn't fall off-screen.
 */
export const PlotTooltip = (props: PlotTooltipProps) => {
  const renderViaPortal = usePlotTooltipPortalEnabled();
  return renderViaPortal ? (
    <PortalledPlotTooltip {...props} />
  ) : (
    <InlinePlotTooltip {...props} />
  );
};

const InlinePlotTooltip = ({ isVisible, x, y, children }: PlotTooltipProps) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [flipLeft, setFlipLeft] = useState(false);

  // `x` is chart-local; the flip decision is viewport-relative, so translate
  // through the chart container's screen rect.
  useLayoutEffect(() => {
    if (!isVisible) return;
    const chartRect = tooltipRef.current?.offsetParent?.getBoundingClientRect();
    const viewportX = chartRect ? chartRect.left + x : x;
    setFlipLeft(viewportX >= window.innerWidth / 2);
  }, [isVisible, x]);

  return (
    <div
      ref={tooltipRef}
      style={{
        display: isVisible ? 'block' : 'none',
        left: `${x}px`,
        top: `${y}px`,
      }}
      className={classNames(styles.tooltip, styles.tooltipInline, {
        // @ts-expect-error noUncheckedIndexedAccess
        [styles.tooltipLeft]: flipLeft,
      })}
      data-testid="plot-tooltip"
    >
      {children}
    </div>
  );
};

type ViewportPlacement = {
  left: number;
  top: number;
  flipLeft: boolean;
};

const PortalledPlotTooltip = ({
  isVisible,
  x,
  y,
  children,
}: PlotTooltipProps) => {
  // The empty <span> is a position probe (not a visual element): its
  // `offsetParent` gives us the chart container's viewport rect, which we
  // need to translate the chart-local `x`/`y` into the viewport coords that
  // `position: fixed` requires.
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [placement, setPlacement] = useState<ViewportPlacement | null>(null);

  useLayoutEffect(() => {
    if (!isVisible) {
      setPlacement(null);
      return;
    }

    const recomputePlacement = () => {
      const chartRect =
        anchorRef.current?.offsetParent?.getBoundingClientRect();
      const left = chartRect ? chartRect.left + x : x;
      const top = chartRect ? chartRect.top + y : y;
      setPlacement({
        left,
        top,
        flipLeft: left >= window.innerWidth / 2,
      });
    };

    recomputePlacement();

    // `position: fixed` doesn't move with the page; recompute on scroll
    // and resize. Capture phase catches scrolls on inner containers
    // (e.g. the drawer body), not just window.
    window.addEventListener('scroll', recomputePlacement, true);
    window.addEventListener('resize', recomputePlacement);
    return () => {
      window.removeEventListener('scroll', recomputePlacement, true);
      window.removeEventListener('resize', recomputePlacement);
    };
  }, [isVisible, x, y]);

  return (
    <>
      <span ref={anchorRef} className={styles.tooltipAnchor} />
      {isVisible && placement
        ? createPortal(
            <div
              style={{
                left: `${placement.left}px`,
                top: `${placement.top}px`,
              }}
              className={classNames(styles.tooltip, styles.tooltipFixed, {
                // @ts-expect-error noUncheckedIndexedAccess
                [styles.tooltipLeft]: placement.flipLeft,
              })}
              data-testid="plot-tooltip"
            >
              {children}
            </div>,
            document.body
          )
        : null}
    </>
  );
};
